/**
 * Where a win stops being a number and becomes an event.
 *
 * A module of its own, with no React and no store in it, because both the
 * overlay and the store need these numbers: the banner and the fanfare are
 * one event. Importing one from the other would tie the store and a
 * component into a cycle for the sake of a constant.
 *
 * Thresholds are multiples of what the round CHARGED, the industry-standard
 * definition — 500 coins is a celebration on a 20-coin bet and a rounding
 * error on a 1000-coin one.
 */
/**
 * Lowered from 15 / 40 / 100 on 2026-08-23.
 *
 * At 15x the lowest tier fired about once in 250 base rounds of Candy
 * Rush; at 8x it is about once in 136 (measured over 500 000 rounds of the
 * shipped math). The celebration is the one piece of feel nobody can judge
 * without seeing it, and once an evening was too rare to judge.
 *
 * This is presentation only: the math, the paytable and the release hashes
 * are untouched, and so is what the server pays.
 */
export const TIERS = [
  { mult: 50, label: 'EPIC WIN', slug: 'epic' },
  { mult: 20, label: 'MEGA WIN', slug: 'mega' },
  { mult: 8, label: 'BIG WIN', slug: 'big' },
] as const

export type Tier = (typeof TIERS)[number]

/**
 * The lowest tier: the point where the fanfare replaces the chime and the
 * banner appears. One constant for both, so tuning the feel cannot leave
 * the sound celebrating a win the screen ignores.
 */
export const BIG_WIN_MULT = TIERS[TIERS.length - 1].mult

/**
 * The tier a round earns, or null when it earns none.
 *
 * Returns the whole tier rather than its label: the overlay needs the
 * label for the word on screen AND the slug for the class that escalates
 * the celebration, and deriving one from the other by slicing the string
 * would put the styling contract inside a `split(' ')`.
 */
export function tierFor(winCoins: number, chargeCoins: number): Tier | null {
  if (chargeCoins <= 0) return null
  const mult = winCoins / chargeCoins
  for (const tier of TIERS) if (mult >= tier.mult) return tier
  return null
}
